
import React, { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem } from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { User, Link as LinkIcon, LogIn, Trash, RefreshCw, ArrowLeft, PlusCircle, X } from "lucide-react";
import { useForm } from "react-hook-form";

interface SlackWorkspace {
  id: string;
  slack_team_name: string;
  slack_team_id: string;
  slack_icon_url: string | null;
  is_primary: boolean;
  notify_enabled: boolean;
  notify_mentions: boolean;
  notify_dm: boolean;
  notify_channel_invites: boolean;
  dm_channel_id: string | null;
  notify_keywords: string[] | null;
  notification_time: string;
}

const SlackIntegration = () => {
  const { currentUser, session } = useAuth();
  const [workspaces, setWorkspaces] = useState<SlackWorkspace[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [connectLoading, setConnectLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [keyword, setKeyword] = useState("");

  // Check for setup parameter on page load
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const isSetup = urlParams.get('setup') === 'true';
    
    if (isSetup) {
      toast({
        title: "💬 Connect Slack",
        description: "Stay updated on important conversations and get notifications right in the chat. Let's connect your Slack workspace!",
        duration: 6000,
      });
      // Clean up the URL parameter
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [toast]);

  const fetchWorkspaces = async () => {
    if (!currentUser) return;
    
    try {
      setLoading(true);
      console.log("Fetching workspaces for user:", currentUser.id);
      
      const { data, error } = await supabase
        .from("slack_workspaces")
        .select("*")
        .eq("user_id", currentUser.id)
        .order("is_primary", { ascending: false });

      if (error) {
        console.error("Error fetching workspaces:", error);
        throw error;
      }
      
      // Transform any string notify_keywords to arrays
      const transformedData = data?.map(workspace => ({
        ...workspace,
        notify_keywords: workspace.notify_keywords || []
      }));
      
      console.log(`Found ${transformedData?.length || 0} workspaces`);
      setWorkspaces(transformedData || []);
    } catch (error: any) {
      console.error("Failed to fetch workspaces:", error);
      toast({
        title: "Error fetching workspaces",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const refreshWorkspaces = async () => {
    setRefreshing(true);
    await fetchWorkspaces();
    setRefreshing(false);
  };

  const handleConnectSlack = async () => {
    if (!currentUser) return;
    
    try {
      setConnectLoading(true);
      console.log("Fetching Slack OAuth URL for user:", currentUser.id);
      
      // If user already has a workspace, disconnect it first
      if (workspaces.length > 0) {
        const confirmReplace = confirm("This will replace your current Slack workspace connection. Continue?");
        if (!confirmReplace) {
          setConnectLoading(false);
          return;
        }
        
        // Delete existing workspace
        const { error: deleteError } = await supabase
          .from("slack_workspaces")
          .delete()
          .eq("user_id", currentUser.id);
          
        if (deleteError) {
          console.error("Error disconnecting existing workspace:", deleteError);
          toast({
            title: "Error disconnecting workspace",
            description: deleteError.message,
            variant: "destructive",
          });
          setConnectLoading(false);
          return;
        }
      }
      
      // Use the exact production domain to match your Slack app configuration
      const redirectUri = `https://sharedmatter.com/slack/callback`;
      console.log("Using EXACT redirect URI:", redirectUri);
      
      // Also log the current origin for comparison
      console.log("Current window.location.origin:", window.location.origin);
      
      // Fetch Slack OAuth URL from our Edge Function
      const { data, error } = await supabase.functions.invoke("get-slack-oauth-url", {
        body: { 
          userId: currentUser.id,
          redirectUri: redirectUri,
          workspaceName: "Slack Workspace"
        }
      });
      
      if (error) {
        console.error("Error invoking get-slack-oauth-url function:", error);
        toast({
          title: "Error preparing Slack connection",
          description: `Function error: ${error.message}`,
          variant: "destructive",
        });
        setConnectLoading(false);
        return;
      }
      
      if (!data || !data.url) {
        console.error("Invalid response from OAuth URL function:", data);
        toast({
          title: "Error preparing Slack connection",
          description: "Invalid response from OAuth URL function",
          variant: "destructive",
        });
        setConnectLoading(false);
        return;
      }
      
      console.log("Generated OAuth URL:", data.url);
      console.log("Redirect URI being used:", data.redirectUri);
      
      // Add a small delay to ensure logs are visible
      setTimeout(() => {
        window.location.href = data.url;
      }, 100);
    } catch (error: any) {
      console.error("Error fetching Slack OAuth URL:", error);
      toast({
        title: "Error preparing Slack connection",
        description: `Could not generate Slack connection URL: ${error.message}`,
        variant: "destructive",
      });
      setConnectLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser) {
      fetchWorkspaces();
    } else {
      setWorkspaces([]);
      setLoading(false);
    }
  }, [currentUser]);

  const setPrimaryWorkspace = async (workspaceId: string) => {
    try {
      setUpdatingId(workspaceId);
      
      // Check if the workspace is already primary
      const targetWorkspace = workspaces.find(w => w.id === workspaceId);
      if (targetWorkspace?.is_primary) {
        console.log("Workspace is already primary");
        toast({
          title: "Workspace is already primary",
          description: "This workspace is already set as your primary workspace."
        });
        setUpdatingId(null);
        return;
      }
      
      console.log("Setting workspace as primary:", workspaceId);
      
      // Call the RPC function directly
      const { error } = await supabase.rpc(
        'set_primary_workspace',
        { 
          workspace_id: workspaceId,
          user_identifier: currentUser?.id
        }
      );
      
      if (error) {
        console.error("Error setting primary workspace:", error);
        throw error;
      }
      
      // Update local state to reflect the change
      setWorkspaces(prevWorkspaces => 
        prevWorkspaces.map(workspace => ({
          ...workspace,
          is_primary: workspace.id === workspaceId
        }))
      );
      
      toast({
        title: "Primary workspace updated",
        description: "Your primary Slack workspace has been updated successfully.",
      });
    } catch (error: any) {
      toast({
        title: "Error updating workspace",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setUpdatingId(null);
    }
  };

  const toggleNotificationSetting = async (workspaceId: string, setting: string, value: boolean | string) => {
    try {
      setUpdatingId(workspaceId);
      console.log(`Updating ${setting} to ${value} for workspace ${workspaceId}`);
      
      const updateData: Record<string, boolean | string> = {};
      updateData[setting] = value;
      
      const { error } = await supabase
        .from("slack_workspaces")
        .update(updateData)
        .eq("id", workspaceId)
        .eq("user_id", currentUser?.id);

      if (error) {
        console.error(`Error updating ${setting}:`, error);
        throw error;
      }
      
      // Update local state
      setWorkspaces(prevWorkspaces => 
        prevWorkspaces.map(workspace => 
          workspace.id === workspaceId 
            ? { ...workspace, [setting]: value } 
            : workspace
        )
      );
      
      toast({
        title: "Preference updated",
        description: "Your notification preference has been updated.",
      });
    } catch (error: any) {
      toast({
        title: "Error updating preference",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setUpdatingId(null);
    }
  };

  const handleAddKeyword = async (workspaceId: string, newKeyword: string) => {
    if (!newKeyword.trim()) {
      toast({
        title: "Invalid keyword",
        description: "Please enter a valid keyword.",
        variant: "destructive",
      });
      return;
    }

    try {
      setUpdatingId(workspaceId);
      
      // Find the workspace
      const workspace = workspaces.find(w => w.id === workspaceId);
      if (!workspace) return;
      
      // Create a new keywords array with the new keyword
      const updatedKeywords = [...(workspace.notify_keywords || []), newKeyword.trim()];
      
      // Update the database
      const { error } = await supabase
        .from("slack_workspaces")
        .update({ notify_keywords: updatedKeywords })
        .eq("id", workspaceId)
        .eq("user_id", currentUser?.id);
        
      if (error) {
        console.error("Error updating keywords:", error);
        throw error;
      }
      
      // Update local state
      setWorkspaces(prevWorkspaces => 
        prevWorkspaces.map(workspace => 
          workspace.id === workspaceId 
            ? { ...workspace, notify_keywords: updatedKeywords } 
            : workspace
        )
      );
      
      // Reset the input field
      setKeyword("");
      
      toast({
        title: "Keyword added",
        description: `"${newKeyword}" added to notification triggers.`,
      });
    } catch (error: any) {
      toast({
        title: "Error adding keyword",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setUpdatingId(null);
    }
  };

  const handleRemoveKeyword = async (workspaceId: string, keywordToRemove: string) => {
    try {
      setUpdatingId(workspaceId);
      
      // Find the workspace
      const workspace = workspaces.find(w => w.id === workspaceId);
      if (!workspace || !workspace.notify_keywords) return;
      
      // Create a new keywords array without the removed keyword
      const updatedKeywords = workspace.notify_keywords.filter(k => k !== keywordToRemove);
      
      // Update the database
      const { error } = await supabase
        .from("slack_workspaces")
        .update({ notify_keywords: updatedKeywords })
        .eq("id", workspaceId)
        .eq("user_id", currentUser?.id);
        
      if (error) {
        console.error("Error removing keyword:", error);
        throw error;
      }
      
      // Update local state
      setWorkspaces(prevWorkspaces => 
        prevWorkspaces.map(workspace => 
          workspace.id === workspaceId 
            ? { ...workspace, notify_keywords: updatedKeywords } 
            : workspace
        )
      );
      
      toast({
        title: "Keyword removed",
        description: `"${keywordToRemove}" removed from notification triggers.`,
      });
    } catch (error: any) {
      toast({
        title: "Error removing keyword",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setUpdatingId(null);
    }
  };

  const deleteWorkspace = async (workspaceId: string) => {
    if (!confirm("Are you sure you want to disconnect this workspace?")) return;
    
    try {
      setUpdatingId(workspaceId);
      console.log(`Deleting workspace ${workspaceId}`);
      
      const { error } = await supabase
        .from("slack_workspaces")
        .delete()
        .eq("id", workspaceId)
        .eq("user_id", currentUser?.id);

      if (error) {
        console.error("Error deleting workspace:", error);
        throw error;
      }
      
      // Update local state
      setWorkspaces(prevWorkspaces => 
        prevWorkspaces.filter(workspace => workspace.id !== workspaceId)
      );
      
      toast({
        title: "Workspace disconnected",
        description: "The Slack workspace has been disconnected.",
      });
    } catch (error: any) {
      toast({
        title: "Error disconnecting workspace",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setUpdatingId(null);
    }
  };

  if (!currentUser) {
    return (
      <div className="container mx-auto p-6 bg-gradient-to-br from-blue-50 via-blue-100 to-blue-50 min-h-screen">
        <Card className="bg-white/80 backdrop-blur-sm">
          <CardHeader>
            <CardTitle>Authentication Required</CardTitle>
            <CardDescription>Please sign in to manage your Slack integrations.</CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center">
            <Link to="/">
              <Button className="bg-blue-500 hover:bg-blue-600">
                <LogIn className="mr-2 h-4 w-4" />
                Sign In
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 bg-gradient-to-br from-blue-50 via-blue-100 to-blue-50 min-h-screen">
      <div className="flex justify-between items-center mb-8">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Link to="/">
              <Button variant="outline" size="icon" aria-label="Back to chat">
                <ArrowLeft className="h-4 w-4" />
              </Button>
            </Link>
            <h1 className="text-3xl font-bold text-blue-800">Slack Integration</h1>
          </div>
          <p className="text-blue-800/80">
            Connect Slack workspaces to receive notifications from Shared Matter.
          </p>
        </div>
        <Button 
          variant="outline" 
          size="icon" 
          onClick={refreshWorkspaces} 
          disabled={refreshing || loading}
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
        </Button>
      </div>

      <div className="grid gap-6">
        {loading ? (
          <Card className="bg-white/80 backdrop-blur-sm">
            <CardHeader>
              <Skeleton className="h-8 w-3/4" />
              <Skeleton className="h-4 w-1/2 mt-2" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-20 w-full" />
            </CardContent>
          </Card>
        ) : workspaces.length > 0 ? (
          workspaces.map((workspace) => (
            <Card key={workspace.id} className="bg-white/80 backdrop-blur-sm">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <div className="flex items-center gap-3">
                  {workspace.slack_icon_url ? (
                    <img 
                      src={workspace.slack_icon_url} 
                      alt={workspace.slack_team_name} 
                      className="w-6 h-6 rounded"
                    />
                  ) : (
                    <div className="w-6 h-6 bg-slate-200 rounded flex items-center justify-center">
                      <User className="w-4 h-4 text-slate-500" />
                    </div>
                  )}
                  <div>
                    <CardTitle className="text-xl">{workspace.slack_team_name}</CardTitle>
                    {workspace.is_primary && (
                      <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">
                        Primary
                      </span>
                    )}
                  </div>
                </div>
                <Button 
                  variant="destructive" 
                  size="sm" 
                  onClick={() => deleteWorkspace(workspace.id)}
                  disabled={updatingId === workspace.id}
                >
                  <Trash className="h-4 w-4" />
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label htmlFor={`notifications-${workspace.id}`} className="flex items-center gap-2">
                      Enable notifications
                    </Label>
                    <Switch
                      id={`notifications-${workspace.id}`}
                      checked={workspace.notify_enabled}
                      disabled={updatingId === workspace.id}
                      onCheckedChange={(checked) => 
                        toggleNotificationSetting(workspace.id, "notify_enabled", checked)
                      }
                    />
                  </div>

                  {workspace.notify_enabled && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor={`time-${workspace.id}`} className="flex items-center gap-2">
                          Morning notification time
                        </Label>
                        <div className="flex items-center gap-2">
                          <Input
                            id={`time-${workspace.id}`}
                            type="time"
                            value={workspace.notification_time || "09:00"}
                            disabled={updatingId === workspace.id}
                            onChange={(e) => 
                              toggleNotificationSetting(workspace.id, "notification_time", e.target.value)
                            }
                            className="w-32"
                          />
                          <span className="text-sm text-muted-foreground">
                            {Intl.DateTimeFormat().resolvedOptions().timeZone}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Remove the "Set as Primary" button since we only allow one workspace */}
                </div>
              </CardContent>
            </Card>
          ))
        ) : (
          <Card className="bg-white/80 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="text-blue-800">No Workspace Connected</CardTitle>
              <CardDescription>Connect a Slack workspace to get started with notifications.</CardDescription>
            </CardHeader>
          </Card>
        )}

        <div className="mt-6 text-center">
          <Button 
            onClick={handleConnectSlack}
            className="gap-2 bg-blue-500 hover:bg-blue-600"
            disabled={connectLoading}
          >
            <LinkIcon className="h-4 w-4" />
            {connectLoading 
              ? 'Connecting...' 
              : workspaces.length > 0 
                ? 'Select a Different Slack Workspace' 
                : 'Connect Slack Workspace'
            }
          </Button>
        </div>
      </div>
    </div>
  );
}

export default SlackIntegration;
